import React, { useState } from "react"
import { Box, Flex, Card, Grid, Text, Button, Tabs, Table, Badge, TextField, Select, Heading } from "@radix-ui/themes"
import { DollarSign, Download, Filter, Search, CreditCard, Wallet, TrendingUp } from "lucide-react"
import PageHeader from "../components/ui/dashboard/PageHeader"
import { useLanguage } from "../contexts/LanguageContext"

const FinancialsPage: React.FC = () => {
    const { t } = useLanguage()

    // Mock data for payouts
    const payouts = [
        { id: 'PO-1001', recipient: 'Burger King', type: 'Restaurant', amount: 1250.00, status: 'processed', date: '2023-10-25', method: 'Bank Transfer' },
        { id: 'PO-1002', recipient: 'Ahmed Driver', type: 'Driver', amount: 450.50, status: 'pending', date: '2023-10-26', method: 'Wallet' },
        { id: 'PO-1003', recipient: 'Pizza Hut', type: 'Restaurant', amount: 2100.25, status: 'processed', date: '2023-10-24', method: 'Bank Transfer' },
        { id: 'PO-1004', recipient: 'Sarah Driver', type: 'Driver', amount: 320.00, status: 'failed', date: '2023-10-26', method: 'Wallet' },
    ]

    return (
        <Flex direction="column" gap="6">
            <PageHeader
                title={t("financials") || "Financials"}
                subtitle="Manage payouts, commissions and transactions"
            />

            {/* Financial Stats */}
            <Grid columns={{ initial: "1", md: "3" }} gap="6">
                <Card>
                    <Flex gap="4" align="center">
                        <Flex
                            justify="center"
                            align="center"
                            className="w-12 h-12 rounded-full bg-green-100 text-green-600"
                        >
                            <DollarSign className="w-6 h-6" />
                        </Flex>
                        <Box>
                            <Text size="2" color="gray">Total Payouts (Monthly)</Text>
                            <Heading size="6">SDG 45,231.00</Heading>
                        </Box>
                    </Flex>
                </Card>
                <Card>
                    <Flex gap="4" align="center">
                        <Flex
                            justify="center"
                            align="center"
                            className="w-12 h-12 rounded-full bg-blue-100 text-blue-600"
                        >
                            <Wallet className="w-6 h-6" />
                        </Flex>
                        <Box>
                            <Text size="2" color="gray">Pending Payouts</Text>
                            <Heading size="6">SDG 3,450.50</Heading>
                        </Box>
                    </Flex>
                </Card>
                <Card>
                    <Flex gap="4" align="center">
                        <Flex
                            justify="center"
                            align="center"
                            className="w-12 h-12 rounded-full bg-purple-100 text-purple-600"
                        >
                            <TrendingUp className="w-6 h-6" />
                        </Flex>
                        <Box>
                            <Text size="2" color="gray">Commission Revenue</Text>
                            <Heading size="6">SDG 12,500.00</Heading>
                        </Box>
                    </Flex>
                </Card>
            </Grid>

            {/* Main Content */}
            <Tabs.Root defaultValue="payouts">
                <Tabs.List>
                    <Tabs.Trigger value="payouts">Payouts</Tabs.Trigger>
                    <Tabs.Trigger value="commissions">Commissions</Tabs.Trigger>
                    <Tabs.Trigger value="transactions">Transactions</Tabs.Trigger>
                </Tabs.List>

                <Box pt="4">
                    <Tabs.Content value="payouts">
                        <Flex direction="column" gap="4">
                            <Card>
                                <Flex justify="between" align="center" gap="4" mb="4">
                                    <Flex gap="3" style={{ flex: 1 }}>
                                        <Box style={{ position: 'relative', flex: 1, maxWidth: '300px' }}>
                                            <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                                            <TextField.Root placeholder="Search recipient..." style={{ paddingLeft: '32px' }} />
                                        </Box>
                                        <Select.Root defaultValue="all">
                                            <Select.Trigger placeholder="Status" />
                                            <Select.Content>
                                                <Select.Item value="all">All Status</Select.Item>
                                                <Select.Item value="pending">Pending</Select.Item>
                                                <Select.Item value="processed">Processed</Select.Item>
                                            </Select.Content>
                                        </Select.Root>
                                    </Flex>
                                    <Button variant="soft">
                                        <Download className="w-4 h-4 mr-2" />
                                        Export
                                    </Button>
                                </Flex>

                                <Table.Root>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.ColumnHeaderCell>ID</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>Recipient</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>Amount</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>Method</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>Date</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {payouts.map(payout => (
                                            <Table.Row key={payout.id}>
                                                <Table.Cell><Text weight="medium">{payout.id}</Text></Table.Cell>
                                                <Table.Cell>{payout.recipient}</Table.Cell>
                                                <Table.Cell>
                                                    <Badge color={payout.type === 'Restaurant' ? 'orange' : 'blue'}>{payout.type}</Badge>
                                                </Table.Cell>
                                                <Table.Cell>SDG {payout.amount.toFixed(2)}</Table.Cell>
                                                <Table.Cell>
                                                    <Flex align="center" gap="2">
                                                        <CreditCard className="w-3 h-3 text-gray-400" />
                                                        {payout.method}
                                                    </Flex>
                                                </Table.Cell>
                                                <Table.Cell>{payout.date}</Table.Cell>
                                                <Table.Cell>
                                                    <Badge color={
                                                        payout.status === 'processed' ? 'green' :
                                                            payout.status === 'pending' ? 'yellow' : 'red'
                                                    }>
                                                        {payout.status}
                                                    </Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Button size="1" variant="ghost">Details</Button>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                    </Table.Body>
                                </Table.Root>
                            </Card>
                        </Flex>
                    </Tabs.Content>

                    <Tabs.Content value="commissions">
                        <Card>
                            <Flex direction="column" gap="4" style={{ maxWidth: '600px' }}>
                                <Heading size="4">Global Commission Settings</Heading>
                                <Text color="gray" size="2">Set the default commission rates for platform transactions.</Text>

                                <Grid columns="2" gap="4">
                                    <Box>
                                        <Text size="2" weight="bold" mb="1">Restaurant Commission (%)</Text>
                                        <TextField.Root defaultValue="15" />
                                        <Text size="1" color="gray" mt="1">Applied to subtotal</Text>
                                    </Box>
                                    <Box>
                                        <Text size="2" weight="bold" mb="1">Driver Commission (%)</Text>
                                        <TextField.Root defaultValue="10" />
                                        <Text size="1" color="gray" mt="1">Applied to delivery fee</Text>
                                    </Box>
                                </Grid>

                                <Flex justify="end" mt="4">
                                    <Button>Save Changes</Button>
                                </Flex>
                            </Flex>
                        </Card>
                    </Tabs.Content>

                    <Tabs.Content value="transactions">
                        <Card>
                            <Flex justify="center" p="8">
                                <Text color="gray">Transaction history log coming soon...</Text>
                            </Flex>
                        </Card>
                    </Tabs.Content>
                </Box>
            </Tabs.Root>
        </Flex>
    )
}

export default FinancialsPage
